import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import StatusBadge from "../../components/common/StatusBadge";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/common/Pagination";
import SearchBar from "../../components/common/Search";
import CustomFilterDropdown from "../../components/common/CustomFilterDropdown";
import ActionMenu from "../../components/common/ActionMenu";

import { AiOutlineEye } from "react-icons/ai";
import { formatDate } from "../../utils/HelperMethod";
import {
  useAdminBookings,
  useAdminVendorsDropdown,
} from "../../custom-hooks/queries";
import { EyeIcon } from "@heroicons/react/24/solid";

// 🔹 Status filter options
const statusOptions = [
  { id: "Pending", name: "Pending" },
  { id: "Approved", name: "Approved" },
  { id: "Upcoming", name: "Upcoming" },
  { id: "Completed", name: "Completed" },
  { id: "Suspended", name: "Suspended" },
  { id: "Cancelled", name: "Cancelled" },
];

// 🔹 Vendor dropdown (abhi mock – baad mein API se aayega)
const vendorOptions = [
  { id: "vendor1", name: "Adventure Co" },
  { id: "vendor2", name: "Desert Fun" },
];

export default function BookingManagement() {
  const navigate = useNavigate();

  // 🔹 State (same pattern as Experience Management)
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(null);
  const [vendorFilter, setVendorFilter] = useState(null);
  const [searchText, setSearchText] = useState("");
  const pageSize = 10;

  const { data: vendorDropdown } = useAdminVendorsDropdown();

  const vendorOptions =
    vendorDropdown?.data?.map((v) => ({
      id: v.vendorId,
      name: v.buisnessName
        ? `${v.buisnessName} (${v.vendorName?.trim() || "Vendor"})`
        : v.vendorName?.trim() || "Vendor",
    })) || [];

  // 🔹 API Query
  const { data, isLoading, isError } = useAdminBookings({
    page: currentPage,
    pageSize,
    search: searchText,
    vendorId: vendorFilter,
    status: statusFilter,
  });

  // 🔹 Map API response → table rows
  const bookings =
    data?.data?.data?.map((item) => ({
      bookingId: item.bookingId,
      bookingNumber: item.bookingNumber,
      experienceTitle: item.experienceTitle,
      vendorName: item.vendorName,
      status: item.status,
      bookingDate: item.bookingDate,
    })) || [];

  // 🔹 Action menu
  const bookingActions = (row) => [
    {
      label: "View Booking",
      icon: <AiOutlineEye />,
      onClick: () => navigate(`/admin/bookings/${row.bookingId}`),
    },
  ];

  // 🔹 Table columns
  const columns = [
    {
      key: "bookingNumber",
      label: "BOOKING NO.",
      width: "1.4fr",
      align: "center",
      render: (row) => (
        <div className="font-semibold text-gray-900 truncate">
          {row.bookingNumber}
        </div>
      ),
    },
    {
      key: "experienceTitle",
      label: "EXPERIENCE",
      width: "1.8fr",
      align: "center",
      render: (row) => (
        <div className="text-gray-700 truncate">{row.experienceTitle}</div>
      ),
    },
    {
      key: "vendorName",
      label: "VENDOR",
      width: "1.5fr",
      align: "center",
      render: (row) => (
        <div className="text-gray-700 truncate">{row.vendorName}</div>
      ),
    },
    {
      key: "createdAt",
      label: "DATE",
      width: "1.2fr",
      align: "center",
      render: (row) => formatDate(row.bookingDate),
    },
    {
      key: "status",
      label: "STATUS",
      width: "1.2fr",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "actions",
      label: "ACTIONS",
      width: "0.8fr",
      align: "center",
      render: (row) => {
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                navigate(`/admin/booking-details/${row.bookingId}`)
              }
              className="p-2 rounded-full bg-[#F3F3F3] hover:bg-gray-200 transition-colors"
              title="View Details"
            >
              <EyeIcon className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        );
      },
    },
  ];

  // 🔹 Handlers
  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (statusId) => {
    setStatusFilter(statusId);
    setCurrentPage(1);
  };

  const handleVendorChange = (vendorId) => {
    setVendorFilter(vendorId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="px-4 py-1 sm:px-6 sm:py-1">
      <PageHeader
        title="Booking Management"
        description="Discover and manage bookings"
      />

      <div className="bg-white rounded-[12px] border border-[#D9D9D9] shadow-sm overflow-hidden mt-4 sm:mt-6">
        {/* 🔍 Search + Filters */}
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 py-6 sm:py-10 px-4 sm:px-6">
          <SearchBar onSearch={handleSearch} />

          <div className="flex flex-col sm:flex-row gap-3">
            <CustomFilterDropdown
              options={statusOptions}
              onChange={handleStatusChange}
              placeholder="Filter by Status"
              valueKey="id"
              labelKey="name"
            />

            <CustomFilterDropdown
              options={vendorOptions}
              onChange={handleVendorChange}
              placeholder="Filter by Vendor"
              valueKey="id"
              labelKey="name"
            />
          </div>
        </div>

        {/* 📋 Table */}
        {isError ? (
          <div className="flex justify-center items-center py-20 text-red-500">
            Failed to load bookings.
          </div>
        ) : (
          <Table
            columns={columns}
            data={bookings}
            isLoading={isLoading}
            skeletonRows={pageSize}
          />
        )}
      </div>

      {/* 📄 Pagination */}
      {!isLoading && !isError && data?.data?.totalPages > 1 && (
        <div className="mt-4 sm:mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={data?.data?.totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}
